// src/components/AdminPortal/Tenants.js
import { useState, useEffect } from "react";
import Axios from "../../../utils/axios";
import { toast } from "react-toastify";
import { FiPlus, FiEdit, FiEye, FiEyeOff, FiCopy } from "react-icons/fi";
import ContentLoader from "../../../global/Content/ContentLoader";
import styles from "./Tenants.module.css";

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    email: "",
    phoneNumber: "",
    countryCode: "91",
    url: "",
    logo: null,
    status: "ACTIVE",
  });

  // Secret Key Popup
  const [secretKeyPopup, setSecretKeyPopup] = useState({
    open: false,
    key: "",
    show: false,
  });

  // Fetch Tenants
  const fetchTenants = async () => {
    try {
      setIsRefreshing(true);

      const response = await Axios.get(`/api/tenant/getAllTenants`);

      const tenantsWithDefaults = (response?.data || [])
        .filter(Boolean)
        .map((t) => ({
          id: t.id ?? "",
          name: t.name ?? "",
          address: t.address ?? "",
          email: t.email ?? "",
          phoneNumber: t.phoneNumber ?? "",
          url: t.url ?? "",

          logo: t.logo
            ? typeof t.logo === "string"
              ? t.logo.startsWith("data:")
                ? t.logo
                : `data:${t.logoType ?? "image/png"};base64,${t.logo}`
              : Array.isArray(t.logo)
              ? URL.createObjectURL(
                  new Blob([new Uint8Array(t.logo)], {
                    type: t.logoType || "image/png",
                  })
                )
              : null
            : null,

          status: t.status ?? "ACTIVE",
          secretKey: t.publicKey ?? "",
        }));

      setTenants(tenantsWithDefaults);
    } catch (err) {
      console.error("fetchTenants error:", err);
      toast.error("Failed to load tenants!");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  // Mask string
  const maskString = (s) =>
    typeof s === "string" && s.length ? "•".repeat(s.length) : "";

  // Generate Secret Key
  const createSecretKey = async (id) => {
    setIsLoading(true);
    try {
      const response = await Axios.post(`/api/tenant/createSecretKey?id=${id}`);

      const secretKey =
        typeof response.data === "string" ? response.data : null;

      if (!secretKey) {
        toast.error("No secret key returned from server!");
        return;
      }

      setTenants((prev) =>
        prev.map((t) => (t.id === id ? { ...t, secretKey } : t))
      );

      setSecretKeyPopup({ open: true, key: secretKey, show: false });

      toast.success("Secret key generated!");
    } catch (err) {
      toast.error("Failed to generate secret key");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle View/Generate secret key
  const handleKeyButton = (tenant) => {
    if (tenant?.secretKey) {
      setSecretKeyPopup({ open: true, key: tenant.secretKey, show: false });
    } else {
      createSecretKey(tenant.id);
    }
  };

  // Create Tenant
  const createTenant = async (tenantData) => {
    try {
      const formDataObj = new FormData();
      Object.keys(tenantData).forEach((key) => {
        if (tenantData[key] !== null && tenantData[key] !== undefined) {
          formDataObj.append(key, tenantData[key]);
        }
      });

      await Axios.post(`/api/tenant/createTenant`, formDataObj, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (err) {
      console.error("createTenant error:", err);
      toast.error("Failed to create tenant!");
      throw err;
    }
  };

  // Update Tenant
  const updateTenant = async (id, tenantData) => {
    try {
      const params = new URLSearchParams({ id, ...tenantData });
      await Axios.put(`/api/tenant/updateTenant?${params.toString()}`, null, {
        headers: { accept: "*/*" },
      });
    } catch (err) {
      toast.error("Failed to update tenant!");
      throw err;
    }
  };

  // Form Change
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "logo") setFormData({ ...formData, logo: files?.[0] || null });
    else setFormData({ ...formData, [name]: value });
  };

  const handleNew = () => {
    setCurrentTenant(null);
    setFormData({
      name: "",
      address: "",
      email: "",
      phoneNumber: "",
      url: "",
      logo: null,
      status: "ACTIVE",
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (tenant) => {
    setCurrentTenant(tenant);
    setFormData({
      name: tenant.name ?? "",
      address: tenant.address ?? "",
      email: tenant.email ?? "",
      phoneNumber: tenant.phoneNumber?.toString() ?? "",
      url: tenant.url ?? "",
      logo: tenant.logo ?? null,
      status: tenant.status ?? "ACTIVE",
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const validateForm = () => {
    const err = {};

    if (!formData.name.trim()) err.name = "Name is required";
    if (!formData.email.trim()) err.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) err.email = "Invalid email";
    if (!String(formData.phoneNumber || "").trim())
      err.phoneNumber = "Phone number is required";

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (currentTenant) {
        await updateTenant(currentTenant.id, formData);
        toast.success("Tenant updated!");
      } else {
        await createTenant(formData);
        toast.success("Tenant created!");
      }
      setIsModalOpen(false);
      fetchTenants();
    } catch {
      toast.error("Operation failed!");
    } finally {
      setIsLoading(false);
    }
  };

  // Filtered List
  const filteredTenants = tenants.filter(
    (t) =>
      t &&
      (t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.url?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="table-container">
      <h2 style={{ marginBottom: 10 }}>Tenants</h2>

      {/* Search + Add */}
      <div style={{ display: "flex", marginBottom: 15 }}>
        <input
          type="text"
          placeholder="Search Tenants..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flexGrow: 1,
            marginRight: 10,
            padding: 8,
            border: "1px solid #ccc",
            borderRadius: 4,
          }}
        />

        <button
          onClick={handleNew}
          disabled={isLoading || isRefreshing}
          className="new-button"
        >
          <FiPlus size={18} />
        </button>
      </div>

      {/* Table */}
      {isRefreshing && !tenants.length ? (
        <ContentLoader />
      ) : filteredTenants.length === 0 ? (
        <div style={{ textAlign: "center", padding: 20, color: "#666" }}>
          No tenants found.
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              {[
                "Name",
                "Address",
                "Email",
                "Phone",
                "Status",
                "Logo",
                "Secret Key",
                "Actions",
              ].map((h) => (
                <th key={h} className={styles.th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredTenants.map((tenant) => (
              <tr key={tenant.id}>
                <td className={styles.td}>{tenant.name}</td>
                <td className={styles.td}>{tenant.address || "N/A"}</td>
                <td className={styles.td}>{tenant.email}</td>
                <td className={styles.td}>{tenant.phoneNumber}</td>
                <td className={styles.td}>{tenant.status}</td>

                {/* Logo */}
                <td className={styles.td}>
                  {tenant.logo ? (
                    <img
                      src={tenant.logo}
                      alt="logo"
                      style={{
                        width: 50,
                        height: 50,
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <span style={{ color: "#999" }}>No Logo</span>
                  )}
                </td>

                {/* FINAL Secret Key Column */}
                <td className={styles.td}>
                  <div className={styles.secretCell}>
                    <span>{tenant.secretKey ? "•••••••••••" : "No Key"}</span>

                    <button
                      onClick={() => handleKeyButton(tenant)}
                      className={
                        tenant.secretKey ? styles.viewBtn : styles.generateBtn
                      }
                    >
                      {tenant.secretKey ? "View" : "Generate"}
                    </button>
                  </div>
                </td>

                {/* Actions */}
                <td className={styles.td}>
                  <button
                    className={styles.editBtn}
                    onClick={() => handleEdit(tenant)}
                  >
                    <FiEdit size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Secret Key Popup */}
      {secretKeyPopup.open && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContainer}>
            <div className={styles.modalHeader}>
              <h2>Tenant Secret Key</h2>
              <button
                onClick={() =>
                  setSecretKeyPopup({ open: false, key: "", show: false })
                }
                className={styles.closeButton}
              >
                &times;
              </button>
            </div>

            {/* Key Box */}
            <div className={styles.keyBox}>
              <span className={styles.keyText}>
                {secretKeyPopup.show
                  ? secretKeyPopup.key
                  : maskString(secretKeyPopup.key)}
              </span>

              <button
                className={styles.showBtn}
                onClick={() =>
                  setSecretKeyPopup((p) => ({ ...p, show: !p.show }))
                }
              >
                {secretKeyPopup.show ? <FiEyeOff /> : <FiEye />}
              </button>

              <button
                className={styles.copyBtn}
                onClick={() => {
                  navigator.clipboard.writeText(secretKeyPopup.key);
                  toast.success("Secret key copied!");
                }}
              >
                <FiCopy />
              </button>
            </div>

            <div className={styles.modalActions}>
              <button
                onClick={() =>
                  setSecretKeyPopup({ open: false, key: "", show: false })
                }
                className={styles.cancelBtn}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContainer}>
            <div className={styles.modalHeader}>
              <h2>{currentTenant ? "Edit Tenant" : "New Tenant"}</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className={styles.closeButton}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <label>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && <p className={styles.error}>{errors.name}</p>}

              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <p className={styles.error}>{errors.email}</p>}

              <label>Phone *</label>
              <input
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
              />
              {errors.phoneNumber && (
                <p className={styles.error}>{errors.phoneNumber}</p>
              )}

              <label>Address *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
              {errors.address && (
                <p className={styles.error}>{errors.address}</p>
              )}

              <label>Country Code *</label>
              <input
                type="text"
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
              />
              {errors.countryCode && (
                <p className={styles.error}>{errors.countryCode}</p>
              )}

              <label>URL</label>
              <input
                type="text"
                name="url"
                value={formData.url}
                onChange={handleChange}
              />

              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>

              <label>Logo</label>
              <input type="file" name="logo" onChange={handleChange} />

              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className={styles.cancelBtn}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={styles.saveBtn}
                >
                  {isLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tenants;
